import { Navigation, Pagination, Zoom } from 'swiper/modules';
import { BaseDialog } from '@/app/base-ui';
import BaseCardMedia from '@/app/base-ui/BaseCardMedia';
import { IMedia } from '@/app/models/media/IMedia';
import { useEffect, useState, useRef } from 'react';
import { CloseIconWhite, NextImageIcon, NextImageIconDisabled, PreviousImageIcon, PreviousImageIconDisabled, ZoomIn } from '@/public/icon';
import CenteredBox from '@/app/base-ui/CenterBox';
import { useTheme, Box, IconButton, Grid, Typography } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/zoom';
import { MAX_HEIGHT_IMG, MAX_SCREEN_WIDTH, MAX_WIDTH_IMG } from '@/app/constants/layoutConfig';
import { TransitionZoom } from '../Transition';

interface ModalViewsMedia {
    mediaUrl: string;
    listMedias: IMedia[];
    initialIndex: number;
    isBlockModalViewMedia?: boolean;
    children: React.ReactNode;
}
// SwiperCore.use([Navigation, Zoom]); // Deprecated in Swiper 11, use modules prop instead

const ModalViewsMedia = ({ mediaUrl, listMedias, initialIndex, isBlockModalViewMedia, children }: ModalViewsMedia) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [isPortrait, setIsPortrait] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const swiperRef = useRef<SwiperCore | null>(null); // Add a ref for Swiper instance
    const isClosingRef = useRef(false); // Prevent rapid close on Android
    const isMountedRef = useRef(false); // Track component mount state

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen]);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    // Improved Swiper cleanup for Android WebView
    useEffect(() => {
        if (!isOpen && swiperRef.current) {
            // Immediately disable touch to prevent zombie events
            if (swiperRef.current.allowTouchMove !== undefined) {
                swiperRef.current.allowTouchMove = false;
            }

            const cleanupTimer = setTimeout(() => {
                if (swiperRef.current && isMountedRef.current) {
                    try {
                        // Force remove all event listeners before destroy
                        const swiperEl = swiperRef.current.el;
                        if (swiperEl) {
                            // Clone and replace to remove all listeners
                            const newEl = swiperEl.cloneNode(true);
                            swiperEl.parentNode?.replaceChild(newEl, swiperEl);
                        }

                        swiperRef.current.destroy(true, true);
                        swiperRef.current = null;
                    } catch (e) {
                        // Force null even on error
                        swiperRef.current = null;
                    }
                }
                isClosingRef.current = false;
            }, 350);

            return () => {
                clearTimeout(cleanupTimer);
                // Emergency cleanup on unmount
                if (swiperRef.current) {
                    try {
                        swiperRef.current.destroy(true, true);
                    } catch (e) {
                        // Silent fail
                    }
                    swiperRef.current = null;
                }
            };
        }
    }, [isOpen]);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const ratio = img.naturalHeight / img.naturalWidth;
        setIsPortrait(ratio >= 1.1);
    };

    const handlePrev = () => {
        if (swiperRef.current) {
            swiperRef.current.slidePrev(); // Use swiperRef to control Swiper navigation
        }
    };

    const handleNext = () => {
        if (swiperRef.current) {
            swiperRef.current.slideNext(); // Use swiperRef to control Swiper navigation
        }
    };

    const handleOpenModal = () => {
        if (isBlockModalViewMedia || isClosingRef.current) return;

        // Debounce modal open to prevent rapid open/close
        isClosingRef.current = true;
        setTimeout(() => {
            if (isMountedRef.current) {
                setIsOpen(true);
                isClosingRef.current = false;
            }
        }, 100);
    };

    return (
        <div style={{ width: '100%' }}>
            <div onClick={handleOpenModal}>{children}</div>
            {/* {isOpen ? ( */}
            <BaseDialog
                fullScreen
                open={isOpen}
                onClose={() => {
                    if (isClosingRef.current) return;
                    isClosingRef.current = true;
                    setIsOpen(false);
                }}
                TransitionComponent={TransitionZoom}
                PaperProps={{ sx: { background: '#000' } }}
                keepMounted={false}
            >
                <style>
                    {`
                        .swiper-zoom-container {
                            cursor: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='8' stroke='white' stroke-width='2' fill='rgba(0%2C0%2C0%2C0.4)'/%3E%3Cline x1='16.5' y1='16.5' x2='22' y2='22' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cline x1='11' y1='8' x2='11' y2='14' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cline x1='8' y1='11' x2='14' y2='11' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") 11 11, zoom-in !important;
                        }
                        .swiper-slide-zoomed .swiper-zoom-container {
                            cursor: grab !important;
                        }
                        .swiper-slide-zoomed .swiper-zoom-container:active {
                            cursor: grabbing !important;
                        }
                    `}
                </style>
                <IconButton
                    onClick={() => {
                        if (isClosingRef.current) return;
                        isClosingRef.current = true;
                        setIsOpen(false);
                    }}
                    sx={{
                        position: 'absolute',
                        top: { mobile: 16, tablet: 20 },
                        left: { mobile: 12, tablet: 20 },
                        zIndex: 100,
                        width: 40,
                        height: 40,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                    }}
                >
                    <CloseIconWhite />
                </IconButton>
                <Grid
                    item
                    className="thumbTiles"
                    sx={{
                        height: '100%',
                        position: 'relative',
                        display: 'flex',
                        width: '100%',
                        maxWidth: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: '#000',
                    }}
                >
                    <Box
                        position="relative"
                        sx={{
                            background: '#000',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Swiper
                            initialSlide={initialIndex}
                            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
                            onSwiper={(swiper) => (swiperRef.current = swiper)} // Capture Swiper instance
                            zoom={{
                                maxRatio: 3,
                                minRatio: 1,
                                toggle: true,
                            }}
                            // pagination={true}
                            observer={true}
                            observeParents={true}
                            observeSlideChildren={true}
                            modules={[Pagination, Zoom]}
                            centeredSlides={false}
                            spaceBetween={0}
                            loop={false}
                            breakpoints={{ 0: { slidesPerView: 1 }, 768: { slidesPerView: 1 } }}
                            style={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background: theme.palette.background.paper,
                                position: 'relative',
                            }}
                        >
                            {listMedias.map((media, index) => (
                                <SwiperSlide key={index}>
                                     <div
                                        className="swiper-zoom-container"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <img
                                            className="swiper-zoom-target"
                                            src={media.mediaUrl as string}
                                            alt="Media"
                                            draggable={false}
                                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            style={{
                                                width: 'auto',
                                                height: 'auto',
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain',
                                            }}
                                            onLoad={handleImageLoad}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                            {listMedias.length > 1 ? (
                                <Box
                                    sx={{
                                        minWidth: '32px',
                                        minHeight: '24px',
                                        top: '10px',
                                        borderRadius: '30px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        right: '20px',
                                        lineHeight: 0,
                                        zIndex: 99,
                                        color: 'white',
                                        position: 'absolute',
                                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                                        padding: '7px 8px',
                                    }}
                                >
                                    <Typography
                                        variant="h2_10Medium"
                                        sx={{ lineHeight: '100%' }}
                                    >{`${currentIndex + 1}/${listMedias.length}`}</Typography>
                                </Box>
                            ) : null}
                        </Swiper>
                        <Box
                            sx={{
                                zIndex: 99,
                                position: 'absolute',
                                right: {mobile: 20, tablet: 140},
                                mr: -2,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: {mobile: 'none', tablet: listMedias.length < 2 ? 'none' : 'block'},
                            }}
                        >
                            {currentIndex === listMedias.length - 1 ? (
                                <IconButton
                                    disabled
                                    onClick={handleNext}
                                >
                                    <NextImageIconDisabled />
                                </IconButton>
                            ) : (
                                <IconButton onClick={handleNext}>
                                    <NextImageIcon />
                                </IconButton>
                            )}
                        </Box>
                        <Box
                            sx={{
                                zIndex: 99,
                                position: 'absolute',
                                left:  {mobile: 20, tablet: 140},
                                ml: -2,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: {mobile: 'none', tablet: listMedias.length < 2 ? 'none' : 'block'},
                            }}
                        >
                            {currentIndex === 0 ? (
                                <IconButton
                                    disabled
                                    onClick={handlePrev}
                                >
                                    <PreviousImageIconDisabled />
                                </IconButton>
                            ) : (
                                <IconButton onClick={handlePrev}>
                                    <PreviousImageIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </BaseDialog>
            {/* ) : null} */}
        </div>
    );
};

export default ModalViewsMedia;
